import type { IssuePoItem } from "../api/mrrInventoryApi";

export type AggregatedPoLine = {
  key: string;
  resourceItemLabel: string;
  standardResourceTitle: string;
  catalogStandardItemId: string;
  resourceItemUnit: string;
  totalQty: number;
  /** Unit rate (₹) from standard resource master at PO generation; PDF uses unless overridden. */
  rate: number;
  /** HSN/SAC from standard resource at PO generation; PDF uses unless overridden in UI. */
  hsnCode: string;
};

export type DetailedPoLine = {
  key: string;
  resourceItemLabel: string;
  standardResourceTitle: string;
  beneficiaryName: string;
  catalogStandardItemId: string;
  resourceItemUnit: string;
  totalQty: number;
  rate: number;
  hsnCode: string;
};

export function aggregateIssuePoItems(items: IssuePoItem[]): AggregatedPoLine[] {
  const map = new Map<string, AggregatedPoLine>();
  for (const it of items) {
    const key = `${it.standardResourceItemId}::${it.catalogStandardItemId}`;
    const cur = map.get(key);
    if (cur) {
      cur.totalQty += it.plannedQty;
    } else {
      map.set(key, {
        key,
        resourceItemLabel: it.resourceItemLabel,
        standardResourceTitle: it.standardResourceTitle,
        catalogStandardItemId: it.catalogStandardItemId,
        resourceItemUnit: it.resourceItemUnit,
        totalQty: it.plannedQty,
        rate: it.resourceItemRate ?? 0,
        hsnCode: (it.hsnCode ?? "").trim(),
      });
    }
  }
  return [...map.values()].sort((a, b) => a.resourceItemLabel.localeCompare(b.resourceItemLabel));
}

export function detailedLinesFromIssuePoItems(items: IssuePoItem[]): DetailedPoLine[] {
  return [...items]
    .sort((a, b) => {
      const bn = (a.beneficiaryName ?? "").localeCompare(b.beneficiaryName ?? "");
      if (bn !== 0) return bn;
      return a.resourceItemLabel.localeCompare(b.resourceItemLabel);
    })
    .map((it) => ({
      key: it.id,
      resourceItemLabel: it.resourceItemLabel,
      standardResourceTitle: it.standardResourceTitle,
      beneficiaryName: it.beneficiaryName ?? it.applicationId,
      catalogStandardItemId: it.catalogStandardItemId,
      resourceItemUnit: it.resourceItemUnit,
      totalQty: it.plannedQty,
      rate: it.resourceItemRate ?? 0,
      hsnCode: (it.hsnCode ?? "").trim(),
    }));
}

export type BeneficiaryAllocation = {
  beneficiaryName: string;
  applicationId: string;
  district: string;
  mandal: string;
  village: string;
  items: { label: string; qty: number; unit: string }[];
};

/** Structured PO payload for fast vector PDF export. */
export type IssuePoPdfDocument = {
  documentTitle: string;
  letterheadTagline: string;
  accentColorHex: string;
  footerNote: string;
  poNumber: string;
  poDate: string;
  buyerName: string;
  buyerAddress: string;
  buyerGstin: string;
  vendorName: string;
  vendorAddress: string;
  vendorGstin: string;
  shipTo: string;
  paymentTerms: string;
  lines: ConstructionPoHtmlLine[];
  taxableTotal: number;
  gstLines: ConstructionPoGstLine[];
  grandTotal: number;
  allocations: BeneficiaryAllocation[];
};

export function getBeneficiaryAllocations(
  items: IssuePoItem[],
  geoByApplicationId?: Map<string, { displayApplicationId: string; district: string; mandal: string; village: string }>,
): BeneficiaryAllocation[] {
  const map = new Map<
    string,
    {
      beneficiaryName: string;
      applicationId: string;
      district: string;
      mandal: string;
      village: string;
      itemsMap: Map<string, { label: string; qty: number; unit: string }>;
    }
  >();

  for (const it of items) {
    const appKey = String(it.applicationId ?? "").trim() || `name::${(it.beneficiaryName ?? "").trim()}`;
    if (!map.has(appKey)) {
      const geo = geoByApplicationId?.get(String(it.applicationId ?? "").trim());
      map.set(appKey, {
        beneficiaryName: (it.beneficiaryName ?? "").trim() || "—",
        applicationId:
          geo?.displayApplicationId ?? ((it.beneficiaryMobileApplicationId ?? "").trim() || appKey),
        district: geo?.district?.trim() || "—",
        mandal: geo?.mandal?.trim() || "—",
        village: geo?.village?.trim() || "—",
        itemsMap: new Map(),
      });
    }
    const row = map.get(appKey)!;
    const itemKey = `${it.standardResourceItemId}::${it.catalogStandardItemId}`;
    if (!row.itemsMap.has(itemKey)) {
      row.itemsMap.set(itemKey, { label: it.resourceItemLabel, qty: 0, unit: it.resourceItemUnit });
    }
    row.itemsMap.get(itemKey)!.qty += it.plannedQty;
  }

  return [...map.values()]
    .map((row) => ({
      beneficiaryName: row.beneficiaryName,
      applicationId: row.applicationId,
      district: row.district,
      mandal: row.mandal,
      village: row.village,
      items: [...row.itemsMap.values()].sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.beneficiaryName.localeCompare(b.beneficiaryName));
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtInr(n: number): string {
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Resolve public paths to absolute URLs so logos render inside the PDF iframe. */
function resolveAssetUrl(url: string): string {
  const t = url.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t) || t.startsWith("data:")) return t;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${t.startsWith("/") ? t : `/${t}`}`;
  }
  return t.startsWith("/") ? t : `/${t}`;
}

function poAccentSafe(hex: string): string {
  const t = hex.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(t)) return t;
  return "#16365f";
}

const LEGACY_MISSING_LOGO_PATH = "/branding/Telangana-LOGO.png";

/** Treat blank / legacy default logo paths as no logo. */
export function normalizeLetterheadLogoUrl(url: string): string {
  const t = url.trim();
  if (!t || t === LEGACY_MISSING_LOGO_PATH) return "";
  return t;
}

function logoCellHtml(url: string, heightPx: number): string {
  const resolved = resolveAssetUrl(normalizeLetterheadLogoUrl(url));
  if (!resolved) return ``;
  const h = Math.min(160, Math.max(24, Math.round(heightPx)));
  return `<img src="${escHtml(resolved)}" alt="" style="height:${h}px;max-width:100%;width:auto;object-fit:contain;display:inline-block;vertical-align:middle;" onerror="this.remove()" />`;
}

export type ConstructionPoHtmlLine = {
  sno: number;
  description: string;
  subDescription: string;
  hsn: string;
  qty: number;
  unit: string;
  rate: number;
  amount: number;
};

export type ConstructionPoGstLine = {
  label: string;
  ratePercent: number;
  amount: number;
};

export type ConstructionDocumentLabels = {
  documentSubtitle: string;
  documentNumberLabel: string;
  documentDateLabel: string;
  vendorBlockTitle: string;
  buyerBlockTitle: string;
  shipToBlockTitle: string;
  signatoryLeftTitle: string;
  signatoryRightTitle: string;
};

export const DEFAULT_PO_DOCUMENT_LABELS: ConstructionDocumentLabels = {
  documentSubtitle: "Official purchase order",
  documentNumberLabel: "PO number",
  documentDateLabel: "PO date",
  vendorBlockTitle: "Vendor (supplier / subcontractor)",
  buyerBlockTitle: "Purchaser / company",
  shipToBlockTitle: "Ship to / site / delivery",
  signatoryLeftTitle: "Purchaser",
  signatoryRightTitle: "Vendor acknowledgement",
};

export const DEFAULT_INVOICE_DOCUMENT_LABELS: ConstructionDocumentLabels = {
  documentSubtitle: "Tax invoice",
  documentNumberLabel: "Invoice number",
  documentDateLabel: "Invoice date",
  vendorBlockTitle: "Bill from (supplier / subcontractor)",
  buyerBlockTitle: "Bill to / company",
  shipToBlockTitle: "Reference / milestone / notes",
  signatoryLeftTitle: "For company",
  signatoryRightTitle: "Supplier signature",
};

/** Shown at the bottom of PO PDFs when no custom footer is configured (no signature blocks). */
export const DEFAULT_PO_DIGITAL_FOOTER =
  "This is a computer-generated purchase order and is valid without a physical signature. Amounts and GST are as per the details recorded at the time of generation.";

export function buildConstructionPoHtml(params: {
  documentTitle: string;
  letterheadTagline: string;
  logoPrimaryUrl: string;
  logoSecondaryUrl: string;
  logoPrimaryHeightPx: number;
  logoSecondaryHeightPx: number;
  accentColorHex: string;
  /** Shown under the totals table; empty uses a short generic line. */
  footerNote: string;
  poNumber: string;
  poDate: string;
  buyerName: string;
  buyerAddress: string;
  buyerGstin: string;
  vendorName: string;
  vendorAddress: string;
  vendorGstin: string;
  shipTo: string;
  paymentTerms: string;
  lines: ConstructionPoHtmlLine[];
  taxableTotal: number;
  gstLines: ConstructionPoGstLine[];
  grandTotal: number;
  /**
   * When true, skips loading Google Fonts in the PDF iframe (major speed-up for html2canvas).
   * Layout, colours, and sizing match the on-screen PO; title/body use the system UI font stack.
   */
  forPdfCapture?: boolean;
  /** Override default PO field labels (e.g. invoice number/date). */
  labels?: Partial<ConstructionDocumentLabels>;
  /** When false, omits the signature lines (invoices use a digital footer only). */
  showSignatures?: boolean;
  /** Fallback footer when `footerNote` is empty (PO vs invoice). */
  defaultFooterNote?: string;
}): string {
  const pdfCap = Boolean(params.forPdfCapture);
  const labels: ConstructionDocumentLabels = {
    ...DEFAULT_PO_DOCUMENT_LABELS,
    ...params.labels,
  };
  const accent = poAccentSafe(params.accentColorHex);
  const logoLeft = logoCellHtml(params.logoPrimaryUrl, params.logoPrimaryHeightPx);
  const logoRight = logoCellHtml(params.logoSecondaryUrl, params.logoSecondaryHeightPx);
  const taglineBlock = params.letterheadTagline.trim()
    ? `<p class="po-tagline">${escHtml(params.letterheadTagline.trim())}</p>`
    : "";

  const rows = params.lines
    .map(
      (ln) => `
    <tr class="po-line">
      <td class="c">${ln.sno}</td>
      <td class="l">
        <div class="desc">${escHtml(ln.description)}</div>
        ${ln.subDescription.trim() ? `<div class="sub">${escHtml(ln.subDescription)}</div>` : ""}
      </td>
      <td class="c mono">${escHtml(ln.hsn || "—")}</td>
      <td class="r mono">${fmtInr(ln.qty)}</td>
      <td class="c">${escHtml(ln.unit)}</td>
      <td class="r mono">${fmtInr(ln.rate)}</td>
      <td class="r mono">${fmtInr(ln.amount)}</td>
    </tr>`,
    )
    .join("");

  const gstRows = params.gstLines
    .map(
      (g) => `
    <tr class="po-total-band">
      <td colspan="6" class="r lbl">${escHtml(g.label)} (${Number.isFinite(g.ratePercent) ? g.ratePercent : 0}%)</td>
      <td class="r mono">${fmtInr(g.amount)}</td>
    </tr>`,
    )
    .join("");

  const footDefault =
    params.defaultFooterNote?.trim() ||
    "This document was generated from the console. Amounts follow rates and GST entered at download.";
  const showSignatures = params.showSignatures !== false;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <title>${escHtml(params.documentTitle)} ${escHtml(params.poNumber)}</title>
  <style>
    ${
      pdfCap
        ? `/* PDF capture: system fonts only — avoids blocking @import in hidden iframe */`
        : `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@500;600;700&display=swap');`
    }
    * { box-sizing: border-box; }
    :root {
      --po-accent: ${accent};
      --po-text: #0f172a;
      --po-text-light: #334155;
      --po-muted: #64748b;
      --po-border: #e2e8f0;
      --po-fill: #f8fafc;
      --po-fill2: #f1f5f9;
      --radius-sm: 6px;
      --radius-md: 10px;
    }
    body {
      font-family: ${
        pdfCap
          ? `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`
          : `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      };
      margin: 0;
      padding: 32px 40px 48px;
      color: var(--po-text);
      font-size: 12.5px;
      line-height: 1.5;
      background: #fff;
    }
    .po-accent-bar { 
      height: 6px; 
      background: ${pdfCap ? "var(--po-accent)" : "linear-gradient(90deg, var(--po-accent), #3b82f6)"}; 
      border-radius: 3px; 
      margin-bottom: 20px; 
    }
    table.po-brand-row {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
    }
    table.po-brand-row td {
      vertical-align: middle;
      border: none;
      padding: 0;
    }
    .po-title-stack { text-align: center; padding: 0 16px; }
    .po-tagline {
      margin: 0 0 10px;
      font-size: 11.5px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: var(--po-muted);
    }
    .po-doc-title {
      font-family: ${pdfCap ? `inherit` : `'Outfit', sans-serif`};
      margin: 0;
      font-size: 28px;
      font-weight: 700;
      letter-spacing: 0.08em;
      color: var(--po-accent);
      line-height: 1;
      white-space: nowrap;
    }
    .po-doc-sub {
      margin: 22px 0 0;
      font-size: 11px;
      letter-spacing: 0.1em;
      color: var(--po-text-light);
      text-transform: uppercase;
      font-weight: 600;
      background: var(--po-fill);
      display: inline-block;
      padding: 4px 12px;
      border-radius: 20px;
      border: 1px solid var(--po-border);
    }
    .po-meta-grid {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      margin-bottom: 24px;
      font-size: 12.5px;
      border: 1px solid var(--po-border);
      border-radius: var(--radius-md);
      overflow: hidden;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.02);
    }
    .po-meta-grid th {
      text-align: left;
      padding: 12px 16px;
      background: var(--po-fill);
      font-weight: 600;
      color: #475569;
      width: 140px;
      border-bottom: 1px solid var(--po-border);
      border-right: 1px solid var(--po-border);
      vertical-align: top;
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .po-meta-grid td {
      padding: 12px 16px;
      border-bottom: 1px solid var(--po-border);
      vertical-align: top;
      color: var(--po-text);
      font-weight: 500;
    }
    .po-meta-grid tr:last-child th,
    .po-meta-grid tr:last-child td { border-bottom: none; }
    .po-meta-grid th:last-child,
    .po-meta-grid td:last-child { border-right: none; }
    .po-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
    .po-card {
      border: 1px solid var(--po-border);
      border-radius: var(--radius-md);
      padding: 18px 20px;
      background: #fff;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
    }
    .po-card h3 {
      margin: 0 0 14px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--po-accent);
      border-bottom: 2px solid var(--po-fill2);
      padding-bottom: 8px;
    }
    .po-card .po-lead { font-size: 14px; font-weight: 700; margin-bottom: 8px; color: var(--po-text); }
    .po-card .po-block { white-space: pre-wrap; color: var(--po-text-light); margin-bottom: 8px; font-size: 12.5px; line-height: 1.6; }
    .po-card .po-gst { font-size: 12px; margin-top: 12px; color: var(--po-text); background: var(--po-fill); padding: 6px 10px; border-radius: var(--radius-sm); border: 1px solid var(--po-border); display: inline-block; }
    .po-full { margin-bottom: 20px; }
    table.po {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 12px;
      margin-top: 12px;
      border: 1px solid var(--po-border);
      border-radius: var(--radius-md);
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
    }
    table.po th {
      background: var(--po-fill);
      color: #475569;
      font-weight: 600;
      font-size: 10.5px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 14px 12px;
      border-bottom: 1px solid var(--po-border);
      text-align: center;
    }
    table.po td {
      border-bottom: 1px solid var(--po-border);
      padding: 12px;
      vertical-align: top;
    }
    table.po th:not(:last-child), table.po td:not(:last-child) {
      border-right: 1px solid var(--po-border);
    }
    table.po tbody tr:last-child td { border-bottom: none; }
    table.po tbody tr.po-line:nth-child(even) { background: #fafbfc; }
    table.po td.l { text-align: left; }
    table.po td.c { text-align: center; }
    table.po td.r { text-align: right; }
    table.po .desc { font-weight: 600; color: var(--po-text); font-size: 12.5px; }
    table.po .sub { font-size: 11px; color: var(--po-muted); margin-top: 4px; line-height: 1.4; }
    table.po td.lbl { font-weight: 600; color: #475569; }
    table.po .mono { font-variant-numeric: tabular-nums; font-family: ui-monospace, Consolas, monospace; }
    table.po tr.po-total-band td { background: var(--po-fill); font-weight: 600; color: var(--po-text); }
    table.po tr.po-grand-total td {
      background: var(--po-accent);
      color: #fff;
      font-size: 14px;
      font-weight: 700;
      border: none;
    }
    table.po tr.po-grand-total td:not(:last-child) { border-right: 1px solid rgba(255,255,255,0.2); }
    table.po tr.po-grand-total td.lbl { color: rgba(255,255,255,0.9); }
    .po-signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      margin-top: 40px;
      page-break-inside: avoid;
    }
    .po-signatures .sig-block { font-size: 11.5px; color: var(--po-muted); text-align: center; }
    .po-signatures .sig-line {
      border-top: 1px dashed #cbd5e1;
      margin-top: 60px;
      padding-top: 12px;
      font-weight: 600;
      color: var(--po-text);
    }
    .foot {
      margin-top: 32px;
      padding-top: 16px;
      border-top: 1px solid var(--po-border);
      font-size: 11px;
      color: var(--po-muted);
      text-align: center;
      line-height: 1.6;
    }
    @media print {
      body { padding: 16px 20px 24px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      tr, .po-card { 
        page-break-inside: avoid; 
        break-inside: avoid;
      }
      table, .po-meta-grid, table.po {
        overflow: visible !important;
      }
    }
    ${
      pdfCap
        ? `
    /* Less compositing for raster capture (html2canvas) */
    .po-meta-grid, table.po, .po-card {
      box-shadow: none !important;
      border-radius: 0 !important;
    }
    table.po { overflow: visible !important; }
    .po-doc-sub {
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
    }
    `
        : ""
    }
  </style>
</head>
<body>
  <div class="po-accent-bar"></div>
  <table class="po-brand-row">
    <tr>
      <td style="width: 33%; text-align: left; vertical-align: middle;">
        ${logoLeft}
      </td>
      <td style="width: 42%; text-align: center; vertical-align: middle;">
        <div class="po-title-stack">
          ${taglineBlock}
          <h1 class="po-doc-title">${escHtml(params.documentTitle)}</h1>
          <div class="po-doc-sub">${escHtml(labels.documentSubtitle)}</div>
        </div>
      </td>
      <td style="width: 25%; text-align: right; vertical-align: middle; padding-right: 12px;">
        ${logoRight}
      </td>
    </tr>
  </table>

  <table class="po-meta-grid">
    <tbody>
      <tr>
        <th scope="row">${escHtml(labels.documentNumberLabel)}</th>
        <td class="mono">${escHtml(params.poNumber)}</td>
        <th scope="row">${escHtml(labels.documentDateLabel)}</th>
        <td>${escHtml(params.poDate)}</td>
      </tr>
    </tbody>
  </table>

  <div class="po-two-col">
    <div class="po-card">
      <h3>${escHtml(labels.vendorBlockTitle)}</h3>
      <div class="po-lead">${escHtml(params.vendorName || "—")}</div>
      <div class="po-block">${escHtml(params.vendorAddress || "")}</div>
      <div class="po-gst"><strong>GSTIN:</strong> ${escHtml(params.vendorGstin || "—")}</div>
    </div>
    <div class="po-card">
      <h3>${escHtml(labels.buyerBlockTitle)}</h3>
      <div class="po-lead">${escHtml(params.buyerName || "—")}</div>
      <div class="po-block">${escHtml(params.buyerAddress || "")}</div>
      <div class="po-gst"><strong>GSTIN:</strong> ${escHtml(params.buyerGstin || "—")}</div>
    </div>
  </div>

  <div class="po-full po-card">
    <h3>${escHtml(labels.shipToBlockTitle)}</h3>
    <div class="po-block">${escHtml(params.shipTo || "—")}</div>
  </div>

  <div class="po-full po-card">
    <h3>Payment &amp; commercial terms</h3>
    <div class="po-block">${escHtml(params.paymentTerms || "—")}</div>
  </div>

  <table class="po">
    <thead>
      <tr>
        <th style="width:38px">S.No</th>
        <th>Description of material / work</th>
        <th style="width:84px">HSN/SAC</th>
        <th style="width:72px">Qty</th>
        <th style="width:52px">Unit</th>
        <th style="width:88px">Rate (₹)</th>
        <th style="width:102px">Amount (₹)</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
      <tr class="po-total-band">
        <td colspan="6" class="r lbl">Taxable value (before tax)</td>
        <td class="r mono">${fmtInr(params.taxableTotal)}</td>
      </tr>
      ${gstRows}
      <tr class="po-grand-total">
        <td colspan="6" class="r lbl">Grand total (₹)</td>
        <td class="r mono">${fmtInr(params.grandTotal)}</td>
      </tr>
    </tbody>
  </table>

  ${
    showSignatures
      ? `<div class="po-signatures">
    <div class="sig-block">
      <div class="sig-line">Authorised signatory — ${escHtml(labels.signatoryLeftTitle)}</div>
    </div>
    <div class="sig-block">
      <div class="sig-line">${escHtml(labels.signatoryRightTitle)} &amp; acceptance</div>
    </div>
  </div>`
      : ""
  }

  <p class="foot">${params.footerNote.trim() ? escHtml(params.footerNote) : escHtml(footDefault)}</p>
</body>
</html>`;
}

/** Landscape annexure only — appended as separate PDF pages after the portrait PO. */
export function buildPoAnnexureHtml(params: {
  allocations: BeneficiaryAllocation[];
  footerNote?: string;
  accentColorHex?: string;
  /** Same as buildConstructionPoHtml — faster PDF capture without remote fonts. */
  forPdfCapture?: boolean;
}): string {
  if (!params.allocations.length) return "";

  const pdfCap = Boolean(params.forPdfCapture);
  const accent = poAccentSafe(params.accentColorHex ?? "#1e40af");
  let annexureRows = "";
  let groupNo = 1;
  for (const alloc of params.allocations) {
    const span = alloc.items.length;
    for (let i = 0; i < span; i++) {
      const item = alloc.items[i];
      const snoCell =
        i === 0 ? `<td rowspan="${span}" class="meta sno-cell">${groupNo}</td>` : "";
      const benCell =
        i === 0
          ? `<td rowspan="${span}" class="meta benef"><strong>${escHtml(alloc.beneficiaryName)}</strong></td>`
          : "";
      const appCell =
        i === 0 ? `<td rowspan="${span}" class="meta mono appid">${escHtml(alloc.applicationId)}</td>` : "";
      const distCell = i === 0 ? `<td rowspan="${span}" class="meta geo">${escHtml(alloc.district)}</td>` : "";
      const mandalCell = i === 0 ? `<td rowspan="${span}" class="meta geo">${escHtml(alloc.mandal)}</td>` : "";
      const villageCell = i === 0 ? `<td rowspan="${span}" class="meta geo">${escHtml(alloc.village)}</td>` : "";
      annexureRows += `
        <tr>
          ${snoCell}
          ${benCell}
          ${appCell}
          ${distCell}
          ${mandalCell}
          ${villageCell}
          <td class="mat">${escHtml(item.label)}</td>
          <td class="r mono qty">${fmtInr(item.qty)} ${escHtml(item.unit)}</td>
        </tr>`;
    }
    groupNo += 1;
  }

  const foot = params.footerNote?.trim()
    ? escHtml(params.footerNote)
    : escHtml("This document was generated from the console. Amounts follow rates and GST entered at download.");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Annexure — Beneficiary allocation</title>
  <style>
    ${
      pdfCap
        ? `/* PDF capture: system fonts only */`
        : `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`
    }
    * { box-sizing: border-box; }
    body {
      font-family: ${
        pdfCap
          ? `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`
          : `'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      };
      margin: 0;
      padding: 18px 22px 28px;
      color: #0f172a;
      font-size: 11px;
      line-height: 1.45;
      background: #fff;
      width: 1040px;
    }
    h1 {
      margin: 0 0 18px;
      font-size: 1.05rem;
      font-weight: 700;
      color: ${accent};
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    .annex-card {
      border-radius: 14px;
      overflow: hidden;
      background: #fff;
      box-shadow: 0 8px 32px rgba(15, 23, 42, 0.07), 0 0 0 1px rgba(148, 163, 184, 0.35);
    }
    table.po.annex-po {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 11px;
      line-height: 1.45;
    }
    table.po.annex-po thead th {
      background: ${accent};
      color: #fff;
      font-size: 9.5px;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      padding: 12px 10px;
      text-align: left;
      border: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.18);
      white-space: nowrap;
    }
    table.po.annex-po thead th:not(:last-child) {
      border-right: 1px solid rgba(255, 255, 255, 0.18);
    }
    table.po.annex-po tbody td {
      padding: 10px 10px;
      vertical-align: middle;
      border-bottom: 1px solid #e8edf4;
      border-right: 1px solid #eef2f7;
      background: #fff;
      color: #0f172a;
    }
    table.po.annex-po tbody td:last-child {
      border-right: none;
    }
    table.po.annex-po tbody tr:last-child td {
      border-bottom: none;
    }
    table.po.annex-po tbody tr:hover td.mat,
    table.po.annex-po tbody tr:hover td.qty {
      background: #f8fafc;
    }
    table.po.annex-po td.sno-cell {
      text-align: center;
      font-weight: 700;
      font-size: 12px;
      color: ${accent};
      background: #f8fafc;
      width: 44px;
      min-width: 44px;
    }
    table.po.annex-po td.benef { min-width: 120px; max-width: 180px; font-weight: 500; }
    table.po.annex-po td.appid { font-size: 10px; max-width: 110px; word-break: break-word; color: #334155; }
    table.po.annex-po td.geo { font-size: 10px; max-width: 92px; word-break: break-word; color: #475569; }
    table.po.annex-po td.mat { font-weight: 500; color: #0f172a; }
    table.po.annex-po td.qty { font-variant-numeric: tabular-nums; }
    .mono { font-family: ui-monospace, Consolas, monospace; font-size: 10px; }
    .foot {
      margin-top: 20px;
      font-size: 10px;
      color: #64748b;
      text-align: center;
      font-style: italic;
    }
  </style>
</head>
<body>
  <h1>Annexure: Beneficiary allocation</h1>
  <div class="annex-card">
  <table class="po annex-po">
    <thead>
      <tr>
        <th style="width: 48px;">S.No</th>
        <th style="min-width: 120px;">Beneficiary name</th>
        <th style="min-width: 96px;">Application ID</th>
        <th style="min-width: 88px;">District</th>
        <th style="min-width: 88px;">Mandal</th>
        <th style="min-width: 88px;">Village</th>
        <th>Material</th>
        <th style="width: 112px;">Allocated qty</th>
      </tr>
    </thead>
    <tbody>
      ${annexureRows}
    </tbody>
  </table>
  </div>
  <p class="foot">${foot}</p>
</body>
</html>`;
}

export type SamplePoPreviewInput = {
  documentTitle: string;
  letterheadTagline: string;
  logoPrimaryUrl: string;
  logoSecondaryUrl: string;
  logoPrimaryHeightPx: number;
  logoSecondaryHeightPx: number;
  accentColorHex: string;
  footerNote: string;
  buyerName: string;
  buyerAddress: string;
  buyerGstin: string;
  defaultVendorName: string;
  defaultVendorAddress: string;
  defaultVendorGstin: string;
  paymentTerms: string;
  defaultShipTo: string;
  gstRows: Array<{ label: string; ratePercent: number }>;
};

const SAMPLE_PO_ALLOCATIONS: BeneficiaryAllocation[] = [
  {
    beneficiaryName: "G. Venkateswarlu",
    applicationId: "APP-SAMPLE-001",
    district: "Nagarkurnool",
    mandal: "Amrabad",
    village: "Mannanur",
    items: [
      { label: "Cement — PPC grade", qty: 450, unit: "Bag" },
      { label: "Steel reinforcement — Fe 500", qty: 1.2, unit: "MT" },
      { label: "River sand — fine grade", qty: 15, unit: "Cum" },
    ],
  },
  {
    beneficiaryName: "K. Lakshmi",
    applicationId: "APP-SAMPLE-002",
    district: "Nagarkurnool",
    mandal: "Achampet",
    village: "Uppununthala",
    items: [
      { label: "Cement — PPC grade", qty: 400, unit: "Bag" },
      { label: "Steel reinforcement — Fe 500", qty: 1.2, unit: "MT" },
      { label: "Machine-made bricks", qty: 12000, unit: "Nos" },
      { label: "CPVC pipes — 1″", qty: 100, unit: "Mtr" },
    ],
  },
  {
    beneficiaryName: "M. Ramesh",
    applicationId: "APP-SAMPLE-003",
    district: "Nagarkurnool",
    mandal: "Kollapur",
    village: "Kollapur",
    items: [
      { label: "Coarse aggregate — 20mm", qty: 12, unit: "Cum" },
      { label: "MS door frame — 4″ × 2.5″", qty: 3, unit: "Nos" },
      { label: "Plywood — BWR grade 18mm", qty: 8, unit: "Sht" },
    ],
  },
  {
    beneficiaryName: "P. Sravani",
    applicationId: "APP-SAMPLE-004",
    district: "Nagarkurnool",
    mandal: "Nagarkurnool",
    village: "Nagarkurnool",
    items: [
      { label: "Exterior emulsion paint", qty: 20, unit: "Ltr" },
      { label: "Electrical wiring — FR PVC", qty: 250, unit: "Mtr" },
      { label: "Coarse aggregate — 20mm", qty: 13, unit: "Cum" },
      { label: "River sand — fine grade", qty: 15, unit: "Cum" },
    ],
  },
];

function samplePoPreviewLines(): ConstructionPoHtmlLine[] {
  return [
    {
      sno: 1,
      description: "Cement — PPC grade (sample line)",
      subDescription: "Standard packing · Illustrative qty & rate only",
      hsn: "25232990",
      qty: 850,
      unit: "Bag",
      rate: 385,
      amount: 850 * 385,
    },
    {
      sno: 2,
      description: "Steel reinforcement — Fe 500 (sample line)",
      subDescription: "As per structural drawing · Illustrative only",
      hsn: "72142090",
      qty: 2.4,
      unit: "MT",
      rate: 62000,
      amount: 2.4 * 62000,
    },
    {
      sno: 3,
      description: "Machine-made bricks (sample line)",
      subDescription: "9″ × 4½″ × 3″ · Illustrative only",
      hsn: "69041000",
      qty: 12000,
      unit: "Nos",
      rate: 9.5,
      amount: 12000 * 9.5,
    },
    {
      sno: 4,
      description: "River sand — fine grade (sample line)",
      subDescription: "Washed & sieved · Illustrative only",
      hsn: "25051000",
      qty: 30,
      unit: "Cum",
      rate: 3200,
      amount: 30 * 3200,
    },
    {
      sno: 5,
      description: "Coarse aggregate — 20mm (sample line)",
      subDescription: "Crushed granite · Illustrative only",
      hsn: "25171010",
      qty: 25,
      unit: "Cum",
      rate: 2800,
      amount: 25 * 2800,
    },
    {
      sno: 6,
      description: "Electrical wiring — FR PVC (sample line)",
      subDescription: "1.5 sq mm copper · Illustrative only",
      hsn: "85444999",
      qty: 500,
      unit: "Mtr",
      rate: 18,
      amount: 500 * 18,
    },
    {
      sno: 7,
      description: "CPVC pipes — 1″ (sample line)",
      subDescription: "SDR 11 · Plumbing · Illustrative only",
      hsn: "39172990",
      qty: 200,
      unit: "Mtr",
      rate: 85,
      amount: 200 * 85,
    },
    {
      sno: 8,
      description: "MS door frame — 4″ × 2.5″ (sample line)",
      subDescription: "Hot rolled · Illustrative only",
      hsn: "73083090",
      qty: 6,
      unit: "Nos",
      rate: 3500,
      amount: 6 * 3500,
    },
    {
      sno: 9,
      description: "Plywood — BWR grade 18mm (sample line)",
      subDescription: "Commercial grade · Illustrative only",
      hsn: "44123900",
      qty: 15,
      unit: "Sht",
      rate: 1450,
      amount: 15 * 1450,
    },
    {
      sno: 10,
      description: "Exterior emulsion paint (sample line)",
      subDescription: "Weathercoat · Illustrative only",
      hsn: "32091000",
      qty: 40,
      unit: "Ltr",
      rate: 320,
      amount: 40 * 320,
    },
  ];
}

function samplePoPreviewTotals(params: SamplePoPreviewInput, lines: ConstructionPoHtmlLine[]) {
  const taxableTotal = lines.reduce((s, ln) => s + ln.amount, 0);
  const gstLines: ConstructionPoGstLine[] = params.gstRows.map((g) => ({
    label: g.label.trim() || "Tax",
    ratePercent: Number.isFinite(g.ratePercent) ? g.ratePercent : 0,
    amount: taxableTotal * ((Number.isFinite(g.ratePercent) ? g.ratePercent : 0) / 100),
  }));
  const grandTotal = taxableTotal + gstLines.reduce((s, x) => s + x.amount, 0);
  return { taxableTotal, gstLines, grandTotal };
}

function samplePoPreviewDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Vector PDF sample (fast download from PO format settings). */
export function buildSampleIssuePoPdfDocument(params: SamplePoPreviewInput): IssuePoPdfDocument {
  const lines = samplePoPreviewLines();
  const { taxableTotal, gstLines, grandTotal } = samplePoPreviewTotals(params, lines);
  return {
    documentTitle: params.documentTitle.trim() || "PURCHASE ORDER",
    letterheadTagline: params.letterheadTagline.trim(),
    accentColorHex: params.accentColorHex,
    footerNote: params.footerNote.trim(),
    poNumber: "PO-SAMPLE-PREVIEW",
    poDate: samplePoPreviewDate(),
    buyerName: params.buyerName.trim() || "(Add purchaser name in PO format settings)",
    buyerAddress: params.buyerAddress.trim() || "Purchaser address will appear here.",
    buyerGstin: params.buyerGstin.trim() || "—",
    vendorName:
      params.defaultVendorName.trim() ||
      "Sample vendor name (set “Default vendor name” in PO format, or live PO uses subcontractor)",
    vendorAddress: params.defaultVendorAddress.trim() || "Default vendor address will appear here.",
    vendorGstin: params.defaultVendorGstin.trim() || "—",
    shipTo: params.defaultShipTo.trim() || "Ship-to / site details will appear here.",
    paymentTerms: params.paymentTerms.trim() || "—",
    lines,
    taxableTotal,
    gstLines,
    grandTotal,
    allocations: SAMPLE_PO_ALLOCATIONS,
  };
}

/** HTML for a downloadable preview using fixed sample line items and your letterhead / GST settings. */
export function buildSamplePoPreviewHtml(
  params: SamplePoPreviewInput,
  opts?: { forPdfCapture?: boolean },
): string {
  const lines = samplePoPreviewLines();
  const { taxableTotal, gstLines, grandTotal } = samplePoPreviewTotals(params, lines);
  const poDate = samplePoPreviewDate();

  return buildConstructionPoHtml({
    documentTitle: params.documentTitle.trim() || "PURCHASE ORDER",
    letterheadTagline: params.letterheadTagline,
    logoPrimaryUrl: params.logoPrimaryUrl,
    logoSecondaryUrl: params.logoSecondaryUrl,
    logoPrimaryHeightPx: params.logoPrimaryHeightPx,
    logoSecondaryHeightPx: params.logoSecondaryHeightPx,
    accentColorHex: params.accentColorHex,
    footerNote: params.footerNote.trim() || DEFAULT_PO_DIGITAL_FOOTER,
    defaultFooterNote: DEFAULT_PO_DIGITAL_FOOTER,
    showSignatures: false,
    poNumber: "PO-SAMPLE-PREVIEW",
    poDate: poDate,
    buyerName: params.buyerName.trim() || "(Add purchaser name in PO format settings)",
    buyerAddress: params.buyerAddress.trim() || "Purchaser address will appear here.",
    buyerGstin: params.buyerGstin.trim() || "—",
    vendorName:
      params.defaultVendorName.trim() ||
      "Sample vendor name (set “Default vendor name” in PO format, or live PO uses subcontractor)",
    vendorAddress: params.defaultVendorAddress.trim() || "Default vendor address will appear here.",
    vendorGstin: params.defaultVendorGstin.trim() || "—",
    shipTo: params.defaultShipTo.trim() || "Ship-to / site details will appear here.",
    paymentTerms: params.paymentTerms.trim() || "—",
    lines,
    taxableTotal,
    gstLines,
    grandTotal,
    forPdfCapture: opts?.forPdfCapture,
  });
}

export function buildSamplePoAnnexurePreviewHtml(
  params: SamplePoPreviewInput,
  opts?: { forPdfCapture?: boolean },
): string {
  return buildPoAnnexureHtml({
    accentColorHex: params.accentColorHex,
    footerNote: params.footerNote,
    allocations: SAMPLE_PO_ALLOCATIONS,
    forPdfCapture: opts?.forPdfCapture,
  });
}
